import React, { useEffect, useState } from "react";
import { Center, Loader, Modal, Stack, Tabs, Text } from "@mantine/core";
import ReportShareRow from "./ReportShareRow";
import ShareReportForm from "./ShareReportForm";
import { ReportShares } from "./useReportShares";

export interface ShareReportModalProps {
  /** Display name of the report being shared; null keeps the modal closed. */
  reportName: string | null;
  onClose: () => void;
  /** Who has it / who can get it, and the share actions (useReportShares). */
  sharing: ReportShares;
}

type ShareTab = "share" | "shared";

/**
 * "Share report" dialog: a loader until the shares arrive, then two tabs.
 * Share — pick staff to send a copy to (no-access roles disabled) and
 * whether their copies sync with this one; closes after a successful share.
 * Shared with — everyone who has a copy; each row's "⋮" menu has Keep in
 * sync and Revoke access.
 */
export default function ShareReportModal({
  reportName,
  onClose,
  sharing,
}: ShareReportModalProps) {
  const opened = reportName !== null;
  const [tab, setTab] = useState<ShareTab>("share");

  // Every opening starts on the Share tab.
  useEffect(() => {
    if (opened) setTab("share");
  }, [opened]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>Share {reportName ?? ""}</Text>}
      centered
    >
      {sharing.loading ? (
        <Center mih={180}>
          <Loader aria-label="Loading shares" />
        </Center>
      ) : (
        <Stack gap="md">
          {sharing.loadError && (
            <Text size="sm" c="var(--color-danger)">
              {sharing.loadError}
            </Text>
          )}
          <Tabs
            value={tab}
            onChange={(v) => setTab((v as ShareTab) ?? "share")}
          >
            <Tabs.List>
              <Tabs.Tab value="share">Share</Tabs.Tab>
              <Tabs.Tab value="shared">
                Shared with ({sharing.shares.length})
              </Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="share" pt="md">
              <ShareReportForm
                candidates={sharing.candidates}
                onShare={sharing.share}
                onShared={onClose}
              />
            </Tabs.Panel>
            <Tabs.Panel value="shared" pt="md">
              <SharedWithList sharing={sharing} />
            </Tabs.Panel>
          </Tabs>
        </Stack>
      )}
    </Modal>
  );
}

function SharedWithList({ sharing }: { sharing: ReportShares }) {
  if (sharing.shares.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        Not shared with anyone yet.
      </Text>
    );
  }
  return (
    <Stack gap="xs">
      {sharing.shares.map((share) => (
        <ReportShareRow
          key={share.id}
          share={share}
          busy={sharing.busyShareId === share.id}
          onSetSync={sharing.setSync}
          onRevoke={sharing.revoke}
        />
      ))}
    </Stack>
  );
}
